// Phase 24a · Public pricing page (read-only in this phase)
// Route: /pricing
// Kill-switch aware: banner "Payments coming soon" if PAYMENTS_ENABLED=false
import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, Zap, Users, Building2, Sparkles, Lock, Coins, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function injectLd(id, obj) {
    let s = document.getElementById(id);
    if (!s) { s = document.createElement('script'); s.type = 'application/ld+json'; s.id = id; document.head.appendChild(s); }
    s.textContent = JSON.stringify(obj);
}
function setDesc(text) {
    let m = document.querySelector('meta[name="description"]');
    if (!m) { m = document.createElement('meta'); m.setAttribute('name','description'); document.head.appendChild(m); }
    m.setAttribute('content', text);
}
function eur(cents) {
    return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

const PLAN_ICON = {
    starter: Zap, pro: Sparkles, business: Users, enterprise: Building2,
};

export default function Pricing() {
    const [catalog, setCatalog] = useState(null);
    const [disabled, setDisabled] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState("");
    const [searchParams] = useSearchParams();
    const [convTracked, setConvTracked] = useState(false);
    const [subsCatalog, setSubsCatalog] = useState([]);
    const [subsEnabled, setSubsEnabled] = useState(false);
    const [interval, setInterval] = useState("month");

    // Phase 24c: load subscription catalog for real premium tier CTAs.
    // F4.4: premium tiers are hidden entirely when PREMIUM_TIERS_ENABLED=false
    // (surfaced via /api/subscriptions/catalog 503 for non-admin). We keep
    // legacy "Coming soon" placeholder rendering (PREMIUM_TIERS from catalog).
    useEffect(() => {
        (async () => {
            try {
                const r = await fetch(`${API}/api/subscriptions/catalog`, { credentials: "include" });
                if (r.ok) {
                    const d = await r.json();
                    setSubsCatalog(d.tiers || []);
                    setSubsEnabled(true);
                }
                // 503 → subsEnabled stays false → "Coming soon" pills render
            } catch {}
        })();
    }, []);

    // Phase 24b · Track upsell conversion when arriving via ?src=upsell_<cta_slug>
    useEffect(() => {
        const src = searchParams.get("src");
        const impression = searchParams.get("impression");
        if (!src || !src.startsWith("upsell_") || convTracked) return;
        const cta = src.replace(/^upsell_/, "");
        (async () => {
            try {
                await fetch(`${API}/api/upsell/events/conversion`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        source_cta_slug: cta,
                        impression_event_id: impression || null,
                        surface: "pricing",
                    }),
                });
                setConvTracked(true);
            } catch {}
        })();
    }, [searchParams, convTracked]);

    useEffect(() => {
        document.title = "Pricing · Global Lead Suite";
        setDesc("Simple plans and credit packs for the Global Lead Suite B2B lead generation platform. Starter $29, Pro $79, Business $149. Credit packs from $19.");
        (async () => {
            try {
                const r = await fetch(`${API}/api/payments/catalog`, { credentials: "include" });
                if (r.status === 503) { setDisabled(true); return; }
                if (r.ok) setCatalog(await r.json());
            } catch {}
        })();
    }, []);

    useEffect(() => {
        if (!catalog) return;
        // JSON-LD Product schema per plan (base plans only, credit packs skipped)
        const products = (catalog.plans || []).map((p) => ({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": p.name,
            "description": (p.features || []).join(", "),
            "brand": { "@type": "Brand", "name": "Global Lead Suite" },
            "offers": {
                "@type": "Offer",
                "price": (p.amount_cents / 100).toFixed(2),
                "priceCurrency": "USD",
                "availability": p.type === "contact"
                    ? "https://schema.org/PreOrder"
                    : "https://schema.org/InStock",
            },
        }));
        injectLd("pricing-ldjson", {
            "@context": "https://schema.org",
            "@graph": products,
        });
        return () => { const s = document.getElementById("pricing-ldjson"); if (s) s.remove(); };
    }, [catalog]);

    const handleCheckout = async (slug) => {
        setCheckoutLoading(slug);
        try {
            // Base plans (starter/pro/business) use the F1-verified subscription-checkout
            // endpoint which returns a real cs_live_* URL. /api/payments/checkout is
            // still used for one-shot credit packs (mode=payment).
            const isBasePlan = ["starter", "pro", "business"].includes(slug);
            if (isBasePlan) {
                const r = await fetch(`${API}/api/billing/subscription-checkout`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ plan_id: slug, interval: "monthly" }),
                });
                if (r.status === 401) { window.location.href = "/"; return; }
                if (r.status === 503) { setDisabled(true); return; }
                if (r.ok) {
                    const d = await r.json();
                    if (d.url) { window.location.href = d.url; return; }
                    alert("Checkout error: missing URL in response");
                } else {
                    const err = await r.text();
                    alert(`Checkout failed (${r.status}): ${err.slice(0, 200)}`);
                }
            } else {
                const r = await fetch(`${API}/api/payments/checkout`, {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        product_slug: slug,
                        success_url: window.location.origin + "/checkout/success",
                        cancel_url: window.location.origin + "/checkout/failed",
                    }),
                });
                if (r.status === 401) { window.location.href = "/"; return; }
                if (r.status === 503) { setDisabled(true); return; }
                if (r.ok) {
                    const d = await r.json();
                    if (d.checkout_url) { window.location.href = d.checkout_url; return; }
                    alert("Checkout error: missing checkout_url in response");
                } else {
                    const err = await r.text();
                    alert(`Checkout failed (${r.status}): ${err.slice(0, 200)}`);
                }
            }
        } catch (e) {
            alert(`Checkout network error: ${e && e.message ? e.message : e}`);
        }
        setCheckoutLoading("");
    };

    // Phase 24c: subscribe to a premium tier
    const handleSubscribe = async (tierSlug) => {
        setCheckoutLoading(tierSlug);
        try {
            const r = await fetch(`${API}/api/subscriptions/checkout`, {
                method: "POST",
                credentials: "include",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    tier_slug: tierSlug, interval,
                    success_url: window.location.origin + "/account/subscriptions",
                    cancel_url: window.location.origin + "/pricing?canceled=1",
                }),
            });
            if (r.status === 401) { window.location.href = "/"; return; }
            if (r.status === 503) { alert("SubscriptionEngine is disabled by the operator."); return; }
            if (r.ok) {
                const d = await r.json();
                // In mock the URL is a fake stripe.mock URL. Simulate by
                // hitting the webhook to auto-activate + redirect to account.
                await fetch(`${API}/api/payments/webhooks/stripe`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        id: `evt_ui_${d.session_id}`,
                        type: "checkout.session.completed",
                        data: { object: { id: d.session_id, subscription: `sub_from_cs_${d.session_id}` } },
                    }),
                });
                window.location.href = "/account/subscriptions?activated=1";
            }
        } catch {}
        setCheckoutLoading("");
    };

    if (disabled) {
        return (
            <div className="min-h-screen bg-[#0B0B0D] text-neutral-100 flex items-center justify-center px-4">
                <div className="text-center max-w-md" data-testid="pricing-disabled">
                    <div className="mx-auto w-16 h-16 rounded-full bg-[rgba(255,255,255,0.08)] border border-[#A3A3A3]/30 flex items-center justify-center mb-4">
                        <Lock className="text-[#A3A3A3]" />
                    </div>
                    <h1 className="text-2xl font-bold mb-2">Payments coming soon</h1>
                    <p className="text-neutral-400 text-sm">
                        The MonetizationEngine is being finalized. You'll be able to
                        subscribe or top up credits shortly.
                    </p>
                    <Link to="/dashboard" className="mt-4 inline-block text-sm text-[#A3A3A3] hover:underline">
                        ← Back to dashboard
                    </Link>
                </div>
            </div>
        );
    }

    if (!catalog) {
        return (
            <div className="min-h-screen bg-[#0B0B0D] text-neutral-100 flex items-center justify-center">
                <div className="text-sm text-neutral-500">Loading pricing…</div>
            </div>
        );
    }

    const isMock = catalog.stripe_mode === "mock";

    return (
        <div className="min-h-screen bg-[#0B0B0D] text-neutral-100">
            <div className="max-w-6xl mx-auto px-4 py-10">
                {/* Hero */}
                <div className="text-center mb-10" data-testid="pricing-hero">
                    {convTracked && (
                        <div className="inline-flex items-center gap-2 rounded-full border border-neutral-500/40 bg-neutral-500/10 px-3 py-1 text-xs text-neutral-300 mb-2" data-testid="pricing-upsell-tracked">
                            <Zap size={12} /> Referral from upsell tracked
                        </div>
                    )}
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#A3A3A3]/30 bg-[#A3A3A3]/5 px-3 py-1 text-xs text-[#A3A3A3] mb-4">
                        <Sparkles size={12} /> Simple, transparent pricing
                    </div>
                    <h1 className="text-4xl sm:text-5xl font-bold mb-3" data-testid="pricing-title">Pricing</h1>
                    <p className="text-neutral-400 max-w-2xl mx-auto">
                        Start free. Upgrade when you're ready. Credit packs on-demand for extra runway.
                    </p>
                    {isMock && (
                        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-neutral-500/40 bg-neutral-500/10 px-3 py-1 text-xs text-neutral-300" data-testid="pricing-mock-badge">
                            <Sparkles size={12} /> STRIPE_MODE=mock · Phase 24a test flow
                        </div>
                    )}
                </div>

                {/* Base plans */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12" data-testid="pricing-plans-grid">
                    {(catalog.plans || []).map((p) => {
                        const Icon = PLAN_ICON[p.slug] || Zap;
                        const highlight = p.slug === "pro";
                        return (
                            <div
                                key={p.slug}
                                data-testid={`plan-card-${p.slug}`}
                                className={`relative rounded-2xl border p-5 flex flex-col transition-all ${
                                    highlight
                                        ? "border-[#A3A3A3]/60 bg-gradient-to-b from-[#A3A3A3]/10 to-[#111114]"
                                        : "border-[rgba(255,255,255,0.08)] bg-[#111114] hover:border-[#A3A3A3]/30"
                                }`}
                            >
                                {highlight && (
                                    <span className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-gradient-to-r from-[#FAFAFA] to-[#A3A3A3] text-white">
                                        Most popular
                                    </span>
                                )}
                                <div className="flex items-center gap-2 mb-3">
                                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#FAFAFA] to-[#A3A3A3] flex items-center justify-center">
                                        <Icon className="text-white" size={18} />
                                    </div>
                                    <h3 className="text-lg font-semibold">{p.name}</h3>
                                </div>
                                <div className="mb-4">
                                    {p.type === "contact" ? (
                                        <p className="text-2xl font-bold">Contact us</p>
                                    ) : (
                                        <p className="text-3xl font-bold">
                                            {eur(p.amount_cents)}<span className="text-sm text-neutral-500 font-normal">/mo</span>
                                        </p>
                                    )}
                                </div>
                                <ul className="text-xs text-neutral-400 space-y-1.5 mb-4 flex-1">
                                    {(p.features || []).map((f) => (
                                        <li key={f} className="flex items-start gap-1.5">
                                            <Check size={12} className="text-[#CCCCCC] mt-0.5 shrink-0" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                {p.type === "contact" ? (
                                    <Link
                                        to="/enterprise"
                                        data-testid={`plan-cta-${p.slug}`}
                                        className="text-center text-xs px-3 py-2 rounded-full border border-[#A3A3A3]/40 hover:bg-[#A3A3A3]/10 transition"
                                    >
                                        Contact sales
                                    </Link>
                                ) : (
                                    <button
                                        onClick={() => handleCheckout(p.slug)}
                                        disabled={checkoutLoading === p.slug}
                                        data-testid={`plan-cta-${p.slug}`}
                                        className={`text-xs px-3 py-2 rounded-full font-medium transition disabled:opacity-50 ${
                                            highlight
                                                ? "bg-gradient-to-r from-[#FAFAFA] to-[#A3A3A3] text-white"
                                                : "border border-[rgba(255,255,255,0.08)] text-neutral-200 hover:border-[#A3A3A3]/40"
                                        }`}
                                    >
                                        {checkoutLoading === p.slug ? "Loading…" : "Choose " + p.name}
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>

                {/* Credit packs */}
                <div className="mb-12" data-testid="pricing-credit-packs">
                    <div className="flex items-center gap-2 mb-4">
                        <Coins className="text-[#A3A3A3]" size={18} />
                        <h2 className="text-sm font-semibold uppercase tracking-widest text-neutral-500">
                            Credit packs · pay-as-you-go
                        </h2>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        {(catalog.credit_packs || []).map((p) => (
                            <div
                                key={p.slug}
                                data-testid={`credit-pack-${p.slug}`}
                                className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#111114] p-4 hover:border-[#A3A3A3]/40 transition-all"
                            >
                                <p className="text-xs text-neutral-500 mb-1">{p.credits.toLocaleString()} credits</p>
                                <p className="text-xl font-bold mb-3">{eur(p.amount_cents)}</p>
                                <button
                                    onClick={() => handleCheckout(p.slug)}
                                    disabled={checkoutLoading === p.slug}
                                    data-testid={`credit-cta-${p.slug}`}
                                    className="w-full text-[11px] px-2 py-1.5 rounded-full border border-[rgba(255,255,255,0.08)] hover:border-[#A3A3A3]/40 transition disabled:opacity-50"
                                >
                                    {checkoutLoading === p.slug ? "Loading…" : "Buy pack"}
                                </button>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Premium add-ons (Phase 24c live) */}
                <div className="mb-6" data-testid="pricing-premium-addons">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <div className="flex items-center gap-2">
                                <Sparkles className="text-[#A3A3A3]" size={18} />
                                <h2 className="text-sm font-semibold uppercase tracking-widest text-neutral-500">
                                    Premium add-ons
                                </h2>
                            </div>
                            <p className="text-xs text-neutral-500 mt-1">
                                Modular tiers you can stack on top of any base plan.
                            </p>
                        </div>
                        {subsEnabled ? (
                            <div className="inline-flex items-center gap-1 rounded-full border border-[rgba(255,255,255,0.08)] bg-[#111114] p-0.5" data-testid="pricing-interval-toggle">
                                <button
                                    onClick={() => setInterval("month")}
                                    data-testid="pricing-interval-monthly"
                                    className={`text-[10px] px-3 py-1 rounded-full transition ${interval === "month" ? "bg-gradient-to-r from-neutral-500 to-neutral-500 text-white" : "text-neutral-400"}`}
                                >
                                    Monthly
                                </button>
                                <button
                                    onClick={() => setInterval("year")}
                                    data-testid="pricing-interval-annual"
                                    className={`text-[10px] px-3 py-1 rounded-full transition inline-flex items-center gap-1 ${interval === "year" ? "bg-gradient-to-r from-neutral-500 to-neutral-500 text-white" : "text-neutral-400"}`}
                                >
                                    Annual <span className="text-[9px] opacity-80">-20%</span>
                                </button>
                            </div>
                        ) : (
                            <span className="text-[10px] px-2 py-0.5 rounded-full border border-[#A3A3A3]/40 bg-[#A3A3A3]/10 text-[#A3A3A3]">
                                Coming soon
                            </span>
                        )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                        {(subsEnabled ? subsCatalog : (catalog.premium_tiers || [])).map((t) => {
                            const price = subsEnabled ? t[interval === "year" ? "annual" : "monthly"] : null;
                            const slug = t.tier_slug || t.slug;
                            const isLoading = checkoutLoading === slug;
                            return (
                                <div
                                    key={slug}
                                    data-testid={`premium-tier-${slug}`}
                                    className={`rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#111114] p-4 ${subsEnabled ? "" : "opacity-70"}`}
                                >
                                    <p className="text-xs text-neutral-300 mb-1">Premium</p>
                                    <h3 className="text-sm font-semibold mb-1">{t.name}</h3>
                                    <p className="text-lg font-bold mb-2">
                                        {subsEnabled
                                            ? <>{eur(price.unit_amount)}<span className="text-[11px] text-neutral-500">/{interval === "year" ? "yr" : "mo"}</span></>
                                            : <>{eur(t.amount_cents)}<span className="text-[11px] text-neutral-500">/mo</span></>
                                        }
                                    </p>
                                    <p className="text-[11px] text-neutral-500 line-clamp-3 mb-3">
                                        {subsEnabled ? (t.features || []).slice(0, 2).join(" · ") : t.description}
                                    </p>
                                    {subsEnabled ? (
                                        <button
                                            onClick={() => handleSubscribe(slug)}
                                            disabled={isLoading}
                                            data-testid={`premium-tier-cta-${slug}`}
                                            className="w-full text-xs px-3 py-1.5 rounded-full bg-gradient-to-r from-neutral-500 to-neutral-500 text-white font-medium disabled:opacity-50"
                                        >
                                            {isLoading ? "Loading…" : `Subscribe → ${t.name.split(" ")[1]}`}
                                        </button>
                                    ) : (
                                        <button disabled data-testid={`premium-tier-cta-${slug}`}
                                            className="w-full text-xs px-3 py-1.5 rounded-full border border-[rgba(255,255,255,0.08)] text-neutral-500">
                                            Coming soon
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Refund policy */}
                <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[#111114] p-5 text-xs text-neutral-400" data-testid="pricing-refund-policy">
                    <h3 className="text-sm font-semibold text-neutral-200 mb-2">24h refund policy</h3>
                    <p>
                        Every purchase is eligible for a self-service refund within 24 hours,
                        no questions asked. After 24h our operator team reviews requests case-by-case.
                        Refunds appear in your original payment method within 5-10 business days.
                    </p>
                    <div className="mt-3 flex items-center gap-1.5 text-[#A3A3A3]">
                        <ArrowRight size={12} />
                        <Link to="/billing" className="hover:underline text-[11px]" data-testid="pricing-billing-link">
                            Manage your subscription →
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
